import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  usePendingPointsApprovals,
  useApprovePointsEntry,
  useRejectPointsEntry,
} from "../hooks/usePoints";
import type { PointsLedgerEntry } from "../services/pointsApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNotification } from "@/shared/hooks/useNotification";

const TYPE_FILTERS = [
  { value: "", label: "All" },
  { value: "Earned", label: "Earned" },
  { value: "Redeemed", label: "Redeemed" },
  { value: "ManualAdjustment", label: "Adjustment" },
  { value: "Reversal", label: "Reversal" },
] as const;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" });

/**
 * Finance queue for pending loyalty-points entries (redemptions, manual
 * adjustments, reversals). Mirrors the wallet approval queue.
 */
export const PointsApprovalsPage = () => {
  const notification = useNotification();
  const [type, setType] = useState("");
  const [customer, setCustomer] = useState("");
  const params = useMemo(() => ({ type: type || undefined, customer: customer || undefined }), [type, customer]);
  const pendingQuery = usePendingPointsApprovals(params);
  const approveMutation = useApprovePointsEntry();
  const rejectMutation = useRejectPointsEntry();

  const [rejectTarget, setRejectTarget] = useState<PointsLedgerEntry | null>(null);
  const [reason, setReason] = useState("");

  const approve = (e: PointsLedgerEntry) =>
    approveMutation
      .mutateAsync({ entryId: e.id, payload: {} })
      .then(() => notification.success(`${e.type} approved.`))
      .catch((err) => {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        notification.error(msg ?? "Approval failed.");
      });

  const reject = () => {
    if (!rejectTarget) return;
    if (reason.trim().length < 10) {
      notification.error("Rejection reason must be at least 10 characters.");
      return;
    }
    rejectMutation
      .mutateAsync({ entryId: rejectTarget.id, payload: { rejectionReason: reason.trim() } })
      .then(() => {
        notification.success("Entry rejected.");
        setRejectTarget(null);
        setReason("");
      })
      .catch(() => notification.error("Rejection failed."));
  };

  const entries = pendingQuery.data ?? [];

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Points Approvals</h1>
        <p className="text-sm text-muted-foreground">
          Pending loyalty-points entries awaiting finance review.{" "}
          {pendingQuery.isFetching && <span>Refreshing…</span>}
        </p>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {TYPE_FILTERS.map((f) => (
            <Button key={f.value} size="sm" variant={type === f.value ? "default" : "outline"} onClick={() => setType(f.value)}>
              {f.label}
            </Button>
          ))}
        </div>
        <Input
          placeholder="Filter by customer name or phone"
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {pendingQuery.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading queue…</div>
      ) : entries.length === 0 ? (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          Nothing pending — finance is up to date.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Submitted</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Cause</TableHead>
              <TableHead>Reason / ref</TableHead>
              <TableHead className="text-right">Points</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="text-xs">{formatDate(e.createdAt)}</TableCell>
                <TableCell>
                  {e.customerId ? (
                    <Link to={`/customers/${e.customerId}?tab=wallet`} className="underline underline-offset-2">
                      {e.customerName || "Open customer"}
                    </Link>
                  ) : (
                    e.customerName || "—"
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={e.direction === "Credit" ? "secondary" : "outline"}>{e.type}</Badge>
                </TableCell>
                <TableCell className="max-w-xs truncate" title={e.cause ?? undefined}>{e.cause}</TableCell>
                <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                  {e.reason ?? e.reference ?? "—"}
                </TableCell>
                <TableCell className={`text-right font-semibold ${e.direction === "Credit" ? "text-emerald-700" : "text-amber-800"}`}>
                  {e.direction === "Credit" ? "+" : "−"} {e.points.toLocaleString("en-KE")}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" onClick={() => approve(e)} disabled={approveMutation.isPending}>
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRejectTarget(e)}>
                    Reject
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject points entry</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Reason (at least 10 characters)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} disabled={rejectMutation.isPending}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
